import { useRecoilState, useRecoilValue } from "recoil";
import {
  artistListState,
  deviceListState,
  pushNotificationLogListState,
  songRequestListState,
  userListState,
} from "../../atom/atom";
import { SongRequest, Status } from "../../types/type";
import { useEffect, useState } from "react";
import {
  updateMakerUserId,
  updateSongRequestStatus,
  updateSongRequestToComplete,
} from "../../api/update.api";
import { sendPushNotification } from "../../api/common.api";
import { formatDate, copyTextToClipboard } from "../../tools/tools";
import dayjs from "dayjs";
import "../styles/SongRequestByUserPage.css";
import CommonButton from "../../components/CommonButton";
import { addMakingDocument } from "../../api/write.api";

import { useToast } from "../../hooks/useToast";
import FormatCopyButton from "./FormatCopyButton";
import SearchModal from "../../components/modal/SearchModal";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { Collections } from "../../types/type";
import {
  fetchAndSetData,
  fetchDeviceList,
  fetchPushNotificationLogList,
  fetchSongRequestList,
  fetchUserList,
} from "../../api/fetch.api";
import Papa from "papaparse";
import _ from "lodash";

// 실패 사유 enum만 여기에 정의
enum FailureReason {
  LACK_OF_DATA = "AI_곡_데이터_부족",
  NO_MODEL = "가수_모델_부재",
  OTHER = "기타",
}

// Status enum은 types/type.ts에서 import된 것을 사용
// import { Status } from "../../types/type"; // 이미 import 되어 있음

function SongRequestPage2() {
  const [songRequestList, setSongRequestList] =
    useRecoilState(songRequestListState);

  const [pushNotificationLogList, setPushNotificationLogList] = useRecoilState(
    pushNotificationLogListState
  );

  // const { userIdToDatesMap } = useMappingUserPushLog();
  // console.log("userIdToDatesMap", userIdToDatesMap);

  const totalArtistList = useRecoilValue(artistListState);
  const [groupedSongRequestList, setGroupedSongRequestList] = useState<{
    [key: string]: SongRequest[];
  }>({});
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [selectedFirstRequestDate, setSelectedFirstRequestDate] = useState(
    dayjs()
  );
  const [isFirstRequestDateFilterOn, setIsFirstRequestDateFilterOn] =
    useState(false);
  const [completeCountFilter, setCompleteCountFilter] = useState<number | null>(
    null
  );
  const [deviceList, setDeviceList] = useRecoilState(deviceListState);
  const [songIdInputs, setSongIdInputs] = useState<{ [key: string]: string }>(
    {}
  );
  const [editingStatus, setEditingStatus] = useState<string | null>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  const [activeSongRequest, setActiveSongRequest] =
    useState<SongRequest | null>(null);
  const [showExistingArtistOnly, setShowExistingArtistOnly] = useState(false);
  const [userCredit, setUserCredit] = useState<number>(0);

  const { fireToast } = useToast();

  const [userList, setUserList] = useRecoilState(userListState);
  const [creditFilter, setCreditFilter] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs(),
    endDate: dayjs(),
  });
  const [isDateRangeFilterOn, setIsDateRangeFilterOn] = useState(false);

  // 메모를 저장할 state 추가
  const [userMemos, setUserMemos] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchAndSetData(fetchSongRequestList, setSongRequestList);
    fetchAndSetData(fetchDeviceList, setDeviceList);
    fetchAndSetData(fetchPushNotificationLogList, setPushNotificationLogList);
    fetchAndSetData(fetchUserList, setUserList);
  }, []);

  const handleSongIdInputChange = (
    songRequestId: string,
    newSongId: string
  ) => {
    setSongIdInputs((prevInputs) => ({
      ...prevInputs,
      [songRequestId]: newSongId,
    }));
  };
  //
  const handleSongIdUpdate = async (
    songRequest: SongRequest,
    fcmToken: string,
    mode: string
  ) => {
    try {
      const newSongId = songIdInputs[songRequest.songRequestId];

      // 기존 API 호출
      await updateSongRequestToComplete({
        songRequestId: songRequest.songRequestId,
        userId: songRequest.userId,
        newSongId: newSongId,
      });

      // UI 업데이트를 위한 userList 상태 업데이트
      setUserList((prevUserList) =>
        prevUserList.map((user) => {
          if (user.userId === songRequest.userId && user.credit?.balance > 0) {
            return {
              ...user,
              credit: {
                ...user.credit,
                balance: user.credit.balance - 1,
              },
            };
          }
          return user;
        })
      );

      // 기존 songRequestList 업데이트
      setSongRequestList((prevList) =>
        prevList.map((request) =>
          request.songRequestId === songRequest.songRequestId
            ? { ...request, songId: newSongId, status: Status.COMPLETE }
            : request
        )
      );

      // FCM 알림 전송 등 나머지 로직...
      const notificationBody = {
        title: "제조 완료",
        body: "내 노래가 제조되었어요. 지금 바로 들어보세요!",
        tokenList: [fcmToken],
        data: {
          songTitle: songRequest.songTitle,
          screen: "MyBrewingList",
          type: "제조완료",
          songId: newSongId,
        },
      };

      await sendPushNotification(notificationBody);

      if (mode === "maker") {
        updateMakerUserId(newSongId, songRequest.userName);
      }

      // 입력 필드 초기화
      setSongIdInputs((prevInputs) => {
        const { [songRequest.songRequestId]: _, ...rest } = prevInputs;
        return rest;
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    // Group requests by user
    let groupedByUser = songRequestList.reduce((acc, request) => {
      const key = `${request.userId}-${request.userName}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(request);
      return acc;
    }, {} as { [key: string]: SongRequest[] });

    // 크레딧 필터 적용
    if (creditFilter !== null) {
      groupedByUser = Object.fromEntries(
        Object.entries(groupedByUser).filter(([key]) => {
          const userId = key.split("-")[0];
          const user = userList.find((user) => user.userId === userId);
          const userCredit = user?.credit?.balance || 0;
          if (creditFilter === 4) {
            return userCredit >= 4;
          }
          if (creditFilter === 5) {
            return userCredit >= 1;
          }
          return userCredit === creditFilter;
        })
      );
    }

    // Apply completeCountFilter
    if (completeCountFilter !== null) {
      groupedByUser = Object.fromEntries(
        Object.entries(groupedByUser).filter(([key, requests]) => {
          const uniqueSongRequests = new Set<string>();

          const completeAndExistingCount = requests.reduce((count, request) => {
            if (
              request.status === Status.COMPLETE ||
              request.status === Status.EXISTING
            ) {
              const songKey = `${request.songTitle}-${request.artistName}`;
              if (!uniqueSongRequests.has(songKey)) {
                uniqueSongRequests.add(songKey);
                return count + 1;
              }
            }
            return count;
          }, 0);

          return (
            (completeCountFilter === 3 && completeAndExistingCount >= 3) ||
            completeAndExistingCount === completeCountFilter
          );
        })
      );
    }

    if (showExistingArtistOnly) {
      groupedByUser = Object.fromEntries(
        Object.entries(groupedByUser).filter(([_, requests]) =>
          requests.some((request) => request.existingArtist !== null)
        )
      );
    }

    // Flatten the filtered requests
    let updatedList = Object.values(groupedByUser).flat();

    // Apply additional filters (artist, status, date)
    if (selectedArtist === "others") {
      const artistNames = totalArtistList.map((artist) => artist.artistName);
      updatedList = updatedList.filter(
        (songRequest) => !artistNames.includes(songRequest.artistName)
      );
    } else if (selectedArtist) {
      updatedList = updatedList.filter(
        (songRequest) => songRequest.artistName === selectedArtist
      );
    }

    if (filterStatus === Status.COMPLETE) {
      updatedList = updatedList.filter(
        (songRequest) => songRequest.status === Status.COMPLETE
      );
    } else if (filterStatus === Status.PENDING) {
      updatedList = updatedList.filter(
        (songRequest) => songRequest.status === Status.PENDING
      );
    } else if (filterStatus === Status.EXISTING) {
      updatedList = updatedList.filter(
        (songrequest) => songrequest.status === Status.EXISTING
      );
    }

    if (isDateRangeFilterOn) {
      updatedList = updatedList.filter((songRequest) => {
        const requestDate = dayjs(songRequest.requestAt.toDate());
        return (
          requestDate.isSame(dateRange.startDate, "day") ||
          requestDate.isSame(dateRange.endDate, "day") ||
          (requestDate.isAfter(dateRange.startDate, "day") &&
            requestDate.isBefore(dateRange.endDate, "day"))
        );
      });
    }

    if (isFirstRequestDateFilterOn) {
      // Filter by user's first song request date
      updatedList = updatedList.filter((songRequest) => {
        const userFirstRequestDate = groupedByUser[
          `${songRequest.userId}-${songRequest.userName}`
        ]
          .map((request) => request.requestAt.toDate())
          .sort((a, b) => a - b)[0];

        return dayjs(userFirstRequestDate).isAfter(
          selectedFirstRequestDate,
          "day"
        );
      });
    }

    // Regroup the filtered requests
    const regroupedRequestList = updatedList.reduce((acc, request) => {
      const key = `${request.userId}-${request.userName}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(request);
      acc[key].sort((a, b) => b.requestAt.toDate() - a.requestAt.toDate());
      return acc;
    }, {} as { [key: string]: SongRequest[] });

    setGroupedSongRequestList(regroupedRequestList);
  }, [
    selectedArtist,
    songRequestList,
    totalArtistList,
    filterStatus,
    dateRange,
    isDateRangeFilterOn,
    completeCountFilter,
    isFirstRequestDateFilterOn,
    selectedFirstRequestDate,
    showExistingArtistOnly,
    creditFilter,
    userList,
  ]);

  const calculateStatusCounts = (requests: SongRequest[]) => {
    const uniqueSongs = new Map<string, Status>();

    requests.forEach((request) => {
      const songKey = `${request.songTitle}-${request.artistName}`;
      const currentStatus = uniqueSongs.get(songKey);

      if (!currentStatus) {
        // 아직 상태가 설정되지 않은 경우
        uniqueSongs.set(songKey, request.status);
      } else {
        // 기존 상태가 있으면 우선순위에 따라 업데이트
        if (request.status === Status.EXISTING) {
          uniqueSongs.set(songKey, Status.EXISTING);
        } else if (
          currentStatus !== Status.EXISTING &&
          request.status === Status.COMPLETE
        ) {
          uniqueSongs.set(songKey, Status.COMPLETE);
        }
      }
    });

    // 상태별로 카운트 계산
    const counts = {
      existing: 0,
      pending: 0,
      completed: 0,
    };

    uniqueSongs.forEach((status) => {
      if (status === Status.EXISTING) {
        counts.existing += 1;
      } else if (status === Status.COMPLETE) {
        counts.completed += 1;
      } else if (status === Status.PENDING) {
        counts.pending += 1;
      }
    });

    return counts;
  };

  const userStatusCounts = Object.entries(
    songRequestList.reduce((acc, request) => {
      const key = `${request.userId}-${request.userName}`;
      if (!acc[key]) {
        // 초기값 설정
        acc[key] = {
          existing: 0,
          pending: 0,
          completed: 0,
          requests: [],
        };
      }
      acc[key].requests.push(request);
      return acc;
    }, {} as { [key: string]: { existing: number; pending: number; completed: number; requests: SongRequest[] } })
  ).reduce((acc, [key, data]) => {
    // calculateStatusCounts 함수 사용하여 상태 카운트 계산
    const counts = calculateStatusCounts(data.requests);
    acc[key] = counts;
    return acc;
  }, {} as { [key: string]: { existing: number; pending: number; completed: number } });

  // 컴포넌트에서 사용할 때는 안전하게 접근
  const getStatusCounts = (key: string) => {
    return userStatusCounts[key] || { existing: 0, pending: 0, completed: 0 };
  };

  const handleFirstRequestDateChange = (direction: "prev" | "next") => {
    setSelectedFirstRequestDate((prevDate) =>
      direction === "prev"
        ? prevDate.subtract(1, "day")
        : prevDate.add(1, "day")
    );
  };
  const handleSongRequestSelect = async (
    songRequest: SongRequest,
    fcmToken: string
  ) => {
    try {
      const makingData = {
        songRequestId: songRequest.songRequestId,
        userId: songRequest.userId,
        userName: songRequest.userName,
        userEmail: songRequest.userEmail,
        userToken: songRequest.userToken || fcmToken,
        songTitle: songRequest.songTitle,
        artistName: songRequest.artistName,
        thumbnailUrl: "",
        existingArtist: songRequest.existingArtist, // 추가된 필드
        otherUserList: songRequest.otherUserList, // 추가된 필드
        status: songRequest.status,
      };
      await addMakingDocument(makingData);
      fireToast({ content: "Making 컬렉션에 추가되었습니다." });
    } catch (error) {
      console.error("Error adding to Making:", error);
    }
  };

  const handleStatusChange = async (
    songRequestId: string,
    userId: string,
    newStatus: Status
  ) => {
    try {
      // FAILED 상태로 변경될 때만 처리
      if (newStatus === Status.FAILED) {
        const songRequest = songRequestList.find(
          (request) => request.songRequestId === songRequestId
        );

        if (!songRequest) {
          console.error("Song request not found");
          return;
        }

        // 먼저 사용자에게 확인
        const shouldProceed = window.confirm("실패 처리를 진행하시겠습니까?");
        if (!shouldProceed) return;

        // 실패 사유 선택
        const reasonInput = window.prompt(
          "실패 사유를 선택해주세요\n1: AI 곡 데이터 부족\n2: 가수 모델 부재\n3: 기타\n4: 알림 없이 상태만 변경",
          "1"
        );

        if (!reasonInput) return; // 취소했을 경우

        let reason: string;
        switch (reasonInput) {
          case "1":
            reason = FailureReason.LACK_OF_DATA;
            break;
          case "2":
            reason = FailureReason.NO_MODEL;
            break;
          case "3":
            const customMessage = window.prompt("메시지를 입력해주세요");
            if (!customMessage) return;
            reason = customMessage;
            break;
          case "4":
            reason = "SILENT";
            break;
          default:
            console.error("Invalid reason selected");
            return;
        }

        // 상태 업데이트
        await updateSongRequestStatus(songRequestId, userId, newStatus);

        // UI 업데이트
        setSongRequestList((prevList) =>
          prevList.map((request) =>
            request.songRequestId === songRequestId
              ? { ...request, status: newStatus }
              : request
          )
        );

        // SILENT가 아닌 경우에만 알림 전송
        if (reason !== "SILENT") {
          const matchingDevice = deviceList.find(
            (device) => device.fcmToken === songRequest.userToken
          );

          if (matchingDevice?.fcmToken) {
            let notificationBody;
            switch (reason) {
              case FailureReason.LACK_OF_DATA:
                notificationBody = {
                  title: "제조 실패",
                  body: `${songRequest.artistName} - ${songRequest.songTitle} 곡을 제작하기 위한 AI 학습 데이터가 부족합니다. 다시 신청해주세요!`,
                  tokenList: [matchingDevice.fcmToken],
                  data: {
                    songTitle: songRequest.songTitle,
                    screen: "MyBrewingList",
                    type: "제조실패",
                  },
                };
                break;
              case FailureReason.NO_MODEL:
                notificationBody = {
                  title: "제조 실패",
                  body: `${songRequest.artistName} - ${songRequest.songTitle} 곡을 제작할 수 있는 가수 모델이 없습니다. 다시 신청해주세요!`,
                  tokenList: [matchingDevice.fcmToken],
                  data: {
                    songTitle: songRequest.songTitle,
                    screen: "MyBrewingList",
                    type: "제조실패",
                  },
                };
                break;
              default: // 기타 사유의 경우
                if (reason !== FailureReason.OTHER) {
                  // 커스텀 메시지인 경우
                  notificationBody = {
                    title: "제조 실패",
                    body: `${songRequest.artistName} - ${songRequest.songTitle}: ${reason}`,
                    tokenList: [matchingDevice.fcmToken],
                    data: {
                      songTitle: songRequest.songTitle,
                      screen: "MyBrewingList",
                      type: "제조실패",
                    },
                  };
                }
                break;
            }

            if (notificationBody) {
              try {
                await sendPushNotification(notificationBody);
              } catch (error) {
                console.error("FCM 알림 전송 실패:", error);
                fireToast({
                  content:
                    "상태는 변경되었으나, 사용자에게 알림 전송을 실패했습니다.",
                  type: "error",
                });
              }
            }
          } else {
            // FCM 토큰이 없는 경우도 error로 표시
            fireToast({
              content: "사용자의 FCM 토큰이 없어 알림을 전송할 수 없습니다.",
              type: "error",
            });
          }
        }
      } else {
        // FAILED가 아닌 다른 상태 변경
        await updateSongRequestStatus(songRequestId, userId, newStatus);
        setSongRequestList((prevList) =>
          prevList.map((request) =>
            request.songRequestId === songRequestId
              ? { ...request, status: newStatus }
              : request
          )
        );
      }
    } catch (error) {
      console.error("Error updating status: ", error);
      alert("상태 업데이트 중 오류가 발생했습니다.");
    }
  };

  const handleStatusClick = (songRequestId: string) => {
    setEditingStatus(songRequestId);
  };

  const handleDateChange = (date: dayjs.Dayjs, type: "start" | "end") => {
    setDateRange((prev) => ({
      ...prev,
      [type === "start" ? "startDate" : "endDate"]: date,
    }));
  };

  useEffect(() => {
    const fetchUserCredit = async () => {
      const currentRequests = Object.values(groupedSongRequestList)[0];

      if (currentRequests && currentRequests.length > 0) {
        const userRef = doc(db, Collections.USER, currentRequests[0].userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserCredit(userDoc.data()?.credit?.balance || 0);
        }
      }
    };

    fetchUserCredit();
  }, [groupedSongRequestList]);

  // 메모 처리 함수 추가
  const handleMemo = (userId: string, type: string) => {
    let memoText = "";

    switch (type) {
      case "guide":
        memoText = "🔺 가이드 완료";
        break;
      case "inference":
        memoText = "⭕️ 추론 요청 완료";
        break;
      case "complete":
        memoText = "✅ 완료";
        break;
      case "custom":
        const customText = window.prompt("메모 내용을 입력해주세요:");
        if (customText) {
          memoText = customText;
        } else {
          return; // 취소하면 함수 종료
        }
        break;
    }

    setUserMemos((prev) => ({
      ...prev,
      [userId]: memoText,
    }));
  };

  const normalizeTitle = (title: any) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9가-힣]/g, "")
      .replace(/\s+/g, "");
  };

  // 제목 유사도 비교 함수
  const areSimilarTitles = (title1: any, title2: any) => {
    const normalized1 = normalizeTitle(title1);
    const normalized2 = normalizeTitle(title2);

    if (normalized1 === normalized2) return true;

    if (
      normalized1.includes(normalized2) ||
      normalized2.includes(normalized1)
    ) {
      return Math.abs(normalized1.length - normalized2.length) < 10;
    }

    return false;
  };

  // 노래 그룹화 및 CSV 생성
  const analyzeSongRequests = (songRequestList: SongRequest[]) => {
    const groups: any = [];
    const processed = new Set();

    songRequestList.forEach((request, index) => {
      if (processed.has(index)) return;

      const group = {
        originalTitles: new Set([request.songTitle]),
        artistNames: new Set([request.artistName]),
        count: 1,
        requests: [request],
      };

      // 나머지 요청들과 비교
      songRequestList.forEach((otherRequest, otherIndex) => {
        if (index === otherIndex || processed.has(otherIndex)) return;

        if (areSimilarTitles(request.songTitle, otherRequest.songTitle)) {
          group.originalTitles.add(otherRequest.songTitle);
          group.artistNames.add(otherRequest.artistName);
          group.count++;
          group.requests.push(otherRequest);
          processed.add(otherIndex);
        }
      });

      groups.push(group);
      processed.add(index);
    });

    // 횟수별로 정렬
    const sortedGroups = _.orderBy(groups, ["count"], ["desc"]);

    // CSV 데이터 준비
    const csvData = sortedGroups.map((group: any) => ({
      "신청 횟수": group.count,
      "노래 제목들": Array.from(group.originalTitles).join(" | "),
      "가수 목록": Array.from(group.artistNames).join(" | "),
      "정규화된 제목": normalizeTitle(Array.from(group.originalTitles)[0]),
    }));

    // CSV 파일 생성 및 다운로드
    const csv = Papa.unparse(csvData);
    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `song_request_analysis_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    analyzeSongRequests(songRequestList);
  }, []);

  return (
    <>
      <div className="container">
        {/* <button onClick={migrateSongRequestStatus}>에러 해결파워</button> */}

        <div className="sort-group">
          <div className="sort-group-title">료 갯수</div>
          <CommonButton
            onClick={() => setCompleteCountFilter(0)}
            selected={completeCountFilter === 0}
          >
            0
          </CommonButton>
          <CommonButton
            onClick={() => setCompleteCountFilter(1)}
            selected={completeCountFilter === 1}
          >
            1
          </CommonButton>
          <CommonButton
            onClick={() => setCompleteCountFilter(2)}
            selected={completeCountFilter === 2}
          >
            2
          </CommonButton>
          <CommonButton
            onClick={() => setCompleteCountFilter(3)}
            selected={completeCountFilter === 3}
          >
            3
          </CommonButton>
          <CommonButton
            onClick={() => setCompleteCountFilter(null)}
            selected={completeCountFilter === null}
          >
            전체
          </CommonButton>
        </div>
        <div className="sort-group">
          <div className="sort-group-title">가수별 정렬</div>
          <CommonButton
            onClick={() => setSelectedArtist(null)}
            selected={selectedArtist === null}
          >
            전체
          </CommonButton>
          <CommonButton
            onClick={() => {
              setShowExistingArtistOnly((prev) => !prev);
            }}
            selected={showExistingArtistOnly}
          >
            existing만
          </CommonButton>
          {totalArtistList.map((artist) => (
            <CommonButton
              key={artist.artistId}
              onClick={() => setSelectedArtist(artist.artistName)}
              selected={selectedArtist === artist.artistName}
            >
              {artist.artistName}
            </CommonButton>
          ))}
          <CommonButton
            onClick={() => setSelectedArtist("others")}
            selected={selectedArtist === "others"}
          >
            Others
          </CommonButton>
        </div>
        <div className="sort-group">
          <div className="sort-group-title">크레딧 필터</div>
          <CommonButton
            onClick={() => setCreditFilter(0)}
            selected={creditFilter === 0}
          >
            0개
          </CommonButton>
          <CommonButton
            onClick={() => setCreditFilter(1)}
            selected={creditFilter === 1}
          >
            1개
          </CommonButton>
          <CommonButton
            onClick={() => setCreditFilter(2)}
            selected={creditFilter === 2}
          >
            2개
          </CommonButton>
          <CommonButton
            onClick={() => setCreditFilter(3)}
            selected={creditFilter === 3}
          >
            3개
          </CommonButton>
          <CommonButton
            onClick={() => setCreditFilter(4)}
            selected={creditFilter === 4}
          >
            4개 이상
          </CommonButton>
          <CommonButton
            onClick={() => setCreditFilter(5)}
            selected={creditFilter === 5}
          >
            1개 이상
          </CommonButton>
          <CommonButton
            onClick={() => setCreditFilter(null)}
            selected={creditFilter === null}
          >
            전체
          </CommonButton>
        </div>
        <div className="sort-group">
          <div className="sort-group-title">날짜 범위 필터</div>
          <input
            type="checkbox"
            checked={isDateRangeFilterOn}
            onChange={(e) => setIsDateRangeFilterOn(e.target.checked)}
          />
          <div className="date-range-selector">
            <input
              type="date"
              value={dateRange.startDate.format("YYYY-MM-DD")}
              onChange={(e) => handleDateChange(dayjs(e.target.value), "start")}
              disabled={!isDateRangeFilterOn}
            />
            <span>~</span>
            <input
              type="date"
              value={dateRange.endDate.format("YYYY-MM-DD")}
              onChange={(e) => handleDateChange(dayjs(e.target.value), "end")}
              disabled={!isDateRangeFilterOn}
            />
          </div>
        </div>
        <div className="sort-group">
          <div className="sort-group-title">신청곡 날짜 필터</div>
          <input
            type="checkbox"
            checked={isFirstRequestDateFilterOn}
            onChange={(e) => setIsFirstRequestDateFilterOn(e.target.checked)}
          />
          <div className="date-selector">
            <button
              onClick={() => handleFirstRequestDateChange("prev")}
              disabled={!isFirstRequestDateFilterOn}
            >
              ◀
            </button>
            <span>{selectedFirstRequestDate.format("YYYY-MM-DD")}</span>
            <button
              onClick={() => handleFirstRequestDateChange("next")}
              disabled={!isFirstRequestDateFilterOn}
            >
              ▶
            </button>
          </div>
        </div>
        <div className="sort-group">
          Status별 정렬
          <CommonButton
            onClick={() => setFilterStatus("all")}
            selected={filterStatus === "all"}
          >
            전체
          </CommonButton>
          <CommonButton
            onClick={() => setFilterStatus(Status.PENDING)}
            selected={filterStatus === Status.PENDING}
          >
            Pending
          </CommonButton>
          <CommonButton
            onClick={() => setFilterStatus(Status.COMPLETE)}
            selected={filterStatus === Status.COMPLETE}
          >
            Complete
          </CommonButton>
          <CommonButton
            onClick={() => setFilterStatus(Status.EXISTING)}
            selected={filterStatus === Status.EXISTING}
          >
            Existing
          </CommonButton>
        </div>
        <div className="sort-group">
          <button onClick={() => setIsSearchModalOpen((prev) => !prev)}>
            검색 달
          </button>
          <FormatCopyButton
            groupedRequests={groupedSongRequestList}
            artistList={totalArtistList}
          />
        </div>
        <div className="sort-group">
          <button
            style={{
              padding: "10px 20px",
              backgroundColor: "#f0f0f0",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={() => {
              const filteredRequests = Object.values(
                groupedSongRequestList
              ).flat();
              const total = filteredRequests.length;
              const pending = filteredRequests.filter(
                (req) => req.status === Status.PENDING
              ).length;
              const complete = filteredRequests.filter(
                (req) => req.status === Status.COMPLETE
              ).length;
              const existing = filteredRequests.filter(
                (req) => req.status === Status.EXISTING
              ).length;

              alert(
                `필터링된 요청 갯수: ${total}개\n- Pending: ${pending}개\n- Complete: ${complete}개\n- Existing: ${existing}개`
              );
            }}
          >
            필터링된 요청 통계 보기
          </button>
        </div>
      </div>

      {Object.entries(groupedSongRequestList).map(([key, requests]) => {
        const latestRequest = requests.reduce((latest, request) =>
          latest.requestAt.toDate() > request.requestAt.toDate()
            ? latest
            : request
        );

        const matchingDevice =
          deviceList.find(
            (device) =>
              device.fcmToken === latestRequest.userToken &&
              device.userName === latestRequest.userName
          ) ||
          deviceList.find(
            (device) => device.fcmToken === latestRequest.userToken
          ) ||
          deviceList.find(
            (device) => device.userName === latestRequest.userName
          );

        const { existing, pending, completed } = getStatusCounts(key);

        const userCredit =
          userList.find((user) => user.userId === requests[0].userId)?.credit
            ?.balance || -9999;

        return (
          <div key={key} className="user-request-container">
            <div className="user-info">
              <p>
                name: {requests[0].userName} / {requests[0].userEmail}
              </p>
              <p style={{ cursor: "pointer" }}>
                Token:{" "}
                {requests.reduce((latest, request) =>
                  latest.requestAt.toDate() > request.requestAt.toDate()
                    ? latest
                    : request
                ).userToken ? (
                  <span
                    onClick={() =>
                      copyTextToClipboard(
                        requests.reduce((latest, request) =>
                          latest.requestAt.toDate() > request.requestAt.toDate()
                            ? latest
                            : request
                        ).userToken
                      )
                    }
                  >
                    {requests
                      .reduce((latest, request) =>
                        latest.requestAt.toDate() > request.requestAt.toDate()
                          ? latest
                          : request
                      )
                      .userToken.substring(0, 10)}
                    ...
                  </span>
                ) : (
                  ""
                )}
              </p>
              {matchingDevice && (
                <p
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    copyTextToClipboard(matchingDevice.hashedDeviceId);
                  }}
                >
                  {matchingDevice.hashedDeviceId}
                </p>
              )}
              <p>
                <span>Ex: {existing}</span>
                {"  /  "}Co:{completed}
                {"  /  "}
                <span style={{ color: "#e74c3c" }}>Pe: {pending}</span>
                {"  /  "}
                <span>크레딧: {userCredit}</span>
              </p>

              {/* 메모가 없을 때만 버튼 그룹 표시 */}
              {!userMemos[key] && (
                <div
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => handleMemo(key, "guide")}>
                      🔺 가이드 완료
                    </button>
                    <button onClick={() => handleMemo(key, "inference")}>
                      ⭕️ 추론 요청 완료
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => handleMemo(key, "complete")}>
                      ✅ 완료
                    </button>
                    <button onClick={() => handleMemo(key, "custom")}>
                      기타
                    </button>
                  </div>
                </div>
              )}

              {/* 메모가 있을 때는 메모와 초기화 버튼 함께 표시 */}
              {userMemos[key] && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>{userMemos[key]}</span>
                  <button
                    onClick={() =>
                      setUserMemos((prev) => {
                        const newMemos = { ...prev };
                        delete newMemos[key];
                        return newMemos;
                      })
                    }
                    style={{
                      marginLeft: "8px",
                      padding: "4px 8px",
                      fontSize: "12px",
                    }}
                  >
                    ↺ 초기화
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "row" }}>
              {requests.map((songRequest) => (
                <div key={songRequest.songRequestId} className="song-request">
                  <p>
                    {songRequest.artistName} - {songRequest.songTitle}
                  </p>
                  <p>
                    Status:
                    {editingStatus === songRequest.songRequestId ? (
                      <select
                        value={songRequest.status}
                        onChange={(e) =>
                          handleStatusChange(
                            songRequest.songRequestId,
                            songRequest.userId,
                            e.target.value as Status
                          )
                        }
                        onBlur={() => setEditingStatus(null)}
                      >
                        <option value={Status.PENDING}>Pending</option>
                        <option value={Status.COMPLETE}>Complete</option>
                        <option value={Status.EXISTING}>Existing</option>
                        <option value={Status.FAILED}>Failed</option>
                        <option value={Status.ERROR}>Error</option>
                      </select>
                    ) : (
                      <span
                        onClick={() =>
                          handleStatusClick(songRequest.songRequestId)
                        }
                      >
                        {songRequest.status}
                      </span>
                    )}
                  </p>
                  <p>날짜: {formatDate(songRequest.requestAt)}</p>
                  <p>
                    Song ID:{" "}
                    {songRequest.songId ? (
                      songRequest.songId
                    ) : (
                      <>
                        <input
                          type="text"
                          value={songIdInputs[songRequest.songRequestId] || ""}
                          onChange={(e) =>
                            handleSongIdInputChange(
                              songRequest.songRequestId,
                              e.target.value
                            )
                          }
                          placeholder="Enter Song ID"
                        />
                        <button
                          onClick={() =>
                            handleSongIdUpdate(
                              songRequest,
                              matchingDevice?.fcmToken || "",
                              "maker"
                            )
                          }
                        >
                          메이커
                        </button>
                        <button
                          onClick={() =>
                            handleSongIdUpdate(
                              songRequest,
                              matchingDevice?.fcmToken || "",
                              "general"
                            )
                          }
                        >
                          일반
                        </button>
                        <button
                          onClick={() =>
                            handleSongRequestSelect(
                              songRequest,
                              matchingDevice?.fcmToken || ""
                            )
                          }
                        >
                          Making 추가
                        </button>
                        <button
                          onClick={() => {
                            setIsSearchModalOpen(true);
                            setActiveSongRequest(songRequest);
                          }}
                        >
                          aws
                        </button>
                      </>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {activeSongRequest && (
        <SearchModal
          isOpen={isSearchModalOpen}
          onClose={() => {
            setIsSearchModalOpen(false);
            setActiveSongRequest(null);
          }}
          activeSongRequest={activeSongRequest}
        />
      )}
    </>
  );
}

export default SongRequestPage2;
